import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HomeIcon, ShieldCheckIcon, LockIcon, TagIcon, UsersIcon, ChevronRightIcon, SearchXIcon } from 'lucide-react';
import { useHousing } from '../../contexts/HousingContext';
import { campusById } from '../../data/institutions';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { PropertyCard } from './PropertyCard';
import { HousingMap } from './HousingMap';
import { HousingFilterChips, HousingSort, HousingViewToggle, MoreFiltersDialog, SearchAsIMove, HousingPrimaryFilters } from './HousingControls';

/**
 * Composition A — the editorial workspace.
 *
 * Leads with the question ("Find your perfect place"), states the result count
 * as a fact beside it, then puts the applied filters directly under the
 * heading so a person can always see what is narrowing their results.
 */
const TRUST = [{
  icon: ShieldCheckIcon,
  title: 'Inspected listings',
  body: 'A representative visited and confirmed the property exists as described.'
}, {
  icon: LockIcon,
  title: 'Safe & secure',
  body: 'Report and block sit on every listing, and your contact details stay yours.'
}, {
  icon: TagIcon,
  title: 'No hidden fees',
  body: 'Rent, deposit and any Campuzon fee are itemised before you apply.'
}, {
  icon: UsersIcon,
  title: 'Student focused',
  body: 'Built for people sharing a home for the first time, not for landlords.'
}];
export function FindHomeVariantA() {
  const {
    results,
    intent,
    activeId,
    setActiveId,
    hoverId,
    setHoverId,
    saved,
    toggleSaved,
    view
  } = useHousing();
  const [moreOpen, setMoreOpen] = useState(false);
  const place = campusById(intent.campusId);
  return <div className="mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6" data-id="element-1820" data-mp="3go415pa3r26e">
      <nav aria-label="Breadcrumb" className="mb-4" data-id="element-1821" data-mp="206q3jjdjfri1">
        <ol className="flex items-center gap-1 text-body-sm text-ink-muted" data-id="element-1822" data-mp="257nlbuumyjj0">
          <li data-id="element-1823" data-mp="aqw6irz9bqox">
            <Link to="/housing" className="hover:text-ink" data-id="element-1824" data-mp="arfebfv70yp8">
              Housing
            </Link>
          </li>
          <li aria-hidden data-id="element-1825" data-mp="5rjabgmhx79o">
            <ChevronRightIcon className="h-4 w-4" data-id="element-1826" data-mp="5scjspcuaavi" />
          </li>
          <li className="font-medium text-ink" data-id="element-1827" data-mp="10b1alp2vnx1">Find a home</li>
        </ol>
      </nav>

      <div className="flex flex-wrap items-start justify-between gap-6" data-id="element-1828" data-mp="3t5ewqcbbzgb5">
        <div className="max-w-xl" data-id="element-1829" data-mp="3t32erz5kp5y3">
          <h1 className="text-h1 font-semibold text-ink" data-id="element-1830" data-mp="3o62a9sodgb93">Find your perfect place</h1>
          <p className="mt-2 text-body-lg text-ink-muted" data-id="element-1831" data-mp="3j6z6sco1id38">
            Discover student accommodation near your campus — and the people you could share it
            with.
          </p>
        </div>
        <div className="rounded-2xl border border-line bg-surface px-5 py-4 text-center" data-id="element-1832" data-mp="3a6j2x23dx3xu">
          <p className="flex items-center gap-2 text-body font-medium text-ink" data-id="element-1833" data-mp="1cfzo9d2c0mxu">
            <HomeIcon aria-hidden className="h-5 w-5 text-brand-700" data-id="element-1834" data-mp="1cdwymu53cvtn" />
            <span className="cz-num" data-id="element-1835" data-mp="18f5z6gd5l23d">{results.length}</span>{' '}
            {results.length === 1 ? 'listing' : 'listings'} found
          </p>
          <p className="mt-1 text-caption text-ink-subtle" data-id="element-1836" data-mp="2vb4574p9qgoo">
            {place ? place.campus.name : 'Your campus'}
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-4" data-id="element-1837" data-mp="302m47n7qe770">
        <HousingFilterChips data-id="element-1838" data-mp="343o5e9l7ktbm" />
        <HousingPrimaryFilters onOpenMore={() => setMoreOpen(true)} data-id="element-1839" data-mp="342mkldee4sgp" />
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-1840" data-mp="27doonruwmohy">
          <HousingSort data-id="element-1841" data-mp="27fhux61lv6pf" />
          <HousingViewToggle data-id="element-1842" data-mp="2cc8bduhbo4bd" />
        </div>
      </div>

      {/* The list is the primary surface; the map is deliberately the narrower
          column so cards never become unusable. */}
      <div className={`mt-6 gap-6 ${view === 'split' ? 'lg:grid lg:grid-cols-[minmax(0,1.9fr)_minmax(320px,1fr)]' : ''}`} data-id="element-1843" data-mp="2ld6k2q2ph00a">
        {view !== 'map' && <div className="min-w-0 space-y-4" data-id="element-1844" data-mp="3rvmjt2fyf4sz">
            {results.length === 0 ? <EmptyState icon={<SearchXIcon className="h-5 w-5" data-id="element-1846" data-mp="3vaxz4zdzsivu" />} title="No homes match this search" description="Try raising the rent ceiling, widening the distance, or removing a required amenity. Your saved homes are untouched." data-id="element-1845" data-mp="p1ylzckyr3v" /> : results.map(listing => <PropertyCard key={listing.id} listing={listing} layout="row" selected={activeId === listing.id} saved={saved.includes(listing.id)} onToggleSave={toggleSaved} onHover={setHoverId} onSelect={setActiveId} data-id="element-1847" data-mp="43tprxudkkk9" />)}
          </div>}

        {view !== 'list' && place && <div className={view === 'map' ? 'mt-4' : ''} data-id="element-1848" data-mp="10um8l38p0qpq">
            <div className="sticky top-24 space-y-4" data-id="element-1849" data-mp="10t2rxbj4kdnf">
              <div className={`relative overflow-hidden rounded-2xl border border-line ${view === 'map' ? 'h-[70dvh]' : 'h-[460px]'}`} data-id="element-1850" data-mp="vwu83ktzfw9x">
                <HousingMap listings={results} campus={{
              lat: place.campus.lat,
              lng: place.campus.lng,
              name: place.campus.name
            }} activeId={activeId} hoverId={hoverId} onSelect={setActiveId} data-id="element-1851" data-mp="1kfw7j2adhjji" />
                <div className="pointer-events-auto absolute bottom-3 left-3 z-[400]" data-id="element-1852" data-mp="11il9fl0h8w5n">
                  <SearchAsIMove data-id="element-1853" data-mp="mt6w61ts5ukz" />
                </div>
              </div>

              {view === 'split' && <section aria-labelledby="why-campuzon-housing" className="rounded-2xl border border-line bg-surface p-5" data-id="element-1854" data-mp="rrqnkhr57ykq">
                  <h2 id="why-campuzon-housing" className="text-h4 font-semibold text-ink" data-id="element-1855" data-mp="irlpiy7uacj8">
                    Why Campuzon Housing?
                  </h2>
                  <ul className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-1856" data-mp="2kic4cjb1ld2k">
                    {TRUST.map(item => <li key={item.title} className="flex gap-3" data-id="element-1857" data-mp="2foq3doe8qxml">
                        <span aria-hidden className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700" data-id="element-1858" data-mp="2arznb12fs216">
                          <item.icon className="h-5 w-5" data-id="element-1859" data-mp="33f3m2oaxrk4i" />
                        </span>
                        <span className="min-w-0" data-id="element-1860" data-mp="2yhtxfavdfchq">
                          <span className="block text-body-sm font-semibold text-ink" data-id="element-1861" data-mp="2ueowvduupkvq">
                            {item.title}
                          </span>
                          <span className="block text-caption leading-snug text-ink-muted" data-id="element-1862" data-mp="2pfkpmsrhlx81">
                            {item.body}
                          </span>
                        </span>
                      </li>)}
                  </ul>
                </section>}
            </div>
          </div>}
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-line bg-canvas p-5" data-id="element-1863" data-mp="3umc67se0apyz">
        <p className="max-w-xl text-body text-ink" data-id="element-1864" data-mp="2ijey2p427ci">
          A home is only half of it. Find people whose budget, schedule and habits actually line up
          with yours.
        </p>
        <span className="flex flex-wrap gap-2" data-id="element-1865" data-mp="c82y79gqk37w">
          <Link to="/housing/roommates" data-id="element-1866" data-mp="cbicia2l5889">
            <Button variant="secondary" icon={<UsersIcon className="h-4 w-4" data-id="element-1868" data-mp="h6e6vgvao7oj" />} data-id="element-1867" data-mp="h7fuekthymn5">
              Find a roommate
            </Button>
          </Link>
          <Link to="/housing/household" data-id="element-1869" data-mp="m57jl72rbunn">
            <Button variant="ghost" data-id="element-1870" data-mp="q96zbng7qpfy">My household</Button>
          </Link>
        </span>
      </div>

      <MoreFiltersDialog open={moreOpen} onClose={() => setMoreOpen(false)} data-id="element-1871" data-mp="2nzgn7moecv97" />
    </div>;
}